import HorizontalBar from "@Components/activity/HorizontalBar";
import VideoInvite from "@Components/activity/VideoInvite";
import VideoWrapper from "@Components/activity/VideoWrapper";
import { IProgressionDurationChartValue, IProgressionSmoothnessChartValue } from "@Components/activity/types";
import LargeSpinner from "@Components/shared/LargeSpinner";
import RadioChart, { IRadioChartData } from "@Components/shared/RadioChart";
import ColumnGraph from "@Components/activity/ColumnGraph";
import HandednessGraph from "@Components/user/HandednessGraph";
import MetricsPercentileGraph from "@Components/activity/MetricsPercentileGraph";
import { QueryKeys, SystemContext } from "@Constants";
import SessionManager from "@Core/session";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IUserConnectionDetails } from "@Models/IUserConnectionDetails";
import ActivityService from "@Services/ActivityService";
import { getActivityMetric, hasCapability, isNil, isPresent, prettifyName, scrollIntoView } from "@Utils";
import { CloseCircleFilled, EditFilled, FlagOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Col, Divider, Popconfirm, Row, Space, Tag, Typography } from "antd";
import moment from "moment";
import React, { memo, useMemo, useState } from "react";
import useActivityView from "@Components/activity/hooks/useActivityView";
import useUpdateActivityMetadata from "@Components/activity/hooks/useUpdateActivityMetadata";
import SignatureWidget from "@Components/activity/SignatureWidget";
import ActivityCopy from "@Components/activity/ActivityCopy";
import { EditActivityMetadata } from "./EditActivityMetadata";

import {
    smoothnessTooltipFormatter,
    smoothnessYAxisLabelFormatterHandler,
    tooltipFormatter,
    tooltipTitle,
    yAxisLabelFormatterHandler,
} from "@Components/activity/helpers";
import NotificationService from "@Services/NotificationService";
import IconButton from "@Components/shared/IconButton";
import useQuestionnaireInstance from "@Components/questionnaires/hooks/useQuestionnaireInstance";
import CMEIndicator from "./CMEIndicator";

const { Title } = Typography;

interface IProps {
    activity: IActivityDetails;
    handleActivitySelect: (userId: string, activityInstanceId: string) => void;
    handleDelete: (activityInstanceId: string) => Promise<void>;
    isActive?: boolean;
    render: boolean;
    userConnections: IUserConnectionDetails[];
    associatedStatus: { status: string };
    isHydrated: boolean;
}

const ActivityView: React.FC<IProps> = ({
    activity,
    render,
    handleDelete,
    handleActivitySelect,
    isActive,
    associatedStatus,
    isHydrated,
    userConnections,
}: IProps) => {
    const { userId } = SessionManager.getServiceUser();
    const [activities, setActivities] = useState<IActivityDetails[]>([activity]);
    const [isEditing, setIsEditing] = useState(false);
    const queryClient = useQueryClient();

    const {
        onAnswerQuestion,
        onComplete,
        onGetStarted,
        isLoadingCreateQuestionnaire,
        isFetching,
        isFetchingMinimal,
        questionnaireInstance,
    } = useQuestionnaireInstance({
        activityCategoryId: activity?.activityCategoryId,
        activityId: activity?.activityId,
        activityInstanceId: activity?.activityInstanceId,
        selectedQuestionnaireId: activity?.activityMeta?.questionnaireId,
    });

    const progressionQuery = useQuery(
        [QueryKeys.ActivityProgression],
        async () => {
            const service = new ActivityService();
            const result = await service.getUser([activity.userId], [activity.activityId], [], "", "", -1);

            if (!result.hasErrors) {
                if (result.value.activities.length > 0) {
                    setActivities(result.value.activities);
                    return result.value.activities;
                }
            }
            return [];
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId && render,
        }
    );

    const deleteActivity = async () => {
        setActivities(activities.filter((x) => x.activityInstanceId !== activity.activityInstanceId));
        // deleting the activity redirects to the activity page
        // ? Review users may prefer to be return to the previous page ie. Profile
        await handleDelete(activity.activityInstanceId);
        //get all notifications and see if there is a notification for the activity. if there is, delete the notification as well.
        const service = new NotificationService();
        const result = await service.getNotifications(SessionManager.getServiceUser()?.userId);
        const notification = result?.value.find(
            (item) => item.payload.category === "activity" && item.payload.entityId === activity.activityInstanceId
        );
        if (notification) {
            await service.setNotificationDeleted(notification.notificationId);
            await queryClient.invalidateQueries([QueryKeys.Notifications]);
        }
    };

    /**
     * Return true if the current activity has a video.
     */
    const hasVideo = useMemo(() => ![null, "", undefined].includes(activity?.mediaUrl), [activity?.mediaUrl]);

    /**
     * Return true if the current activityinstance's meta has a description or tags.
     */
    const hasMeta = () => {
        return isPresent(activity?.activityMeta?.description) || isPresent(activity?.activityMeta?.tags);
    };

    /**
     * Return true if a smoothness timeline signature exists for this activity instance.
     */
    const hasSignature = () => {
        return isPresent(activity?.activitySignature);
    };

    /**
     * Render a video feature for the current activity instance.
     */

    const renderVideo = (activity: IActivityDetails) => {
        return !isNil(activity) && hasVideo ? (
            <VideoWrapper
                activity={activity}
                isActive={isActive}
                isFetching={isFetching}
                hasVideo={hasVideo}
                isLoadingCreateQuestionnaire={isLoadingCreateQuestionnaire}
                onAnswerQuestion={onAnswerQuestion}
                onComplete={onComplete}
                onGetStarted={onGetStarted}
                questionnaireInstance={questionnaireInstance}
                associatedStatus={associatedStatus}
                isHydrated={isHydrated}
            />
        ) : (
            <div style={{ maxWidth: "400px" }}>
                {questionnaireInstance && (isOwner || activity?.activityMeta?.isCMEPublic) ? (
                    <CMEIndicator
                        isFetching={isFetching}
                        isLoadingCreateQuestionnaire={isLoadingCreateQuestionnaire}
                        isCMEPublic={activity?.activityMeta?.isCMEPublic}
                        hasVideo={hasVideo}
                        onAnswerQuestion={onAnswerQuestion}
                        onComplete={onComplete}
                        onGetStarted={onGetStarted}
                        questionnaireInstanceData={questionnaireInstance}
                        associatedStatus={associatedStatus}
                        isAssociatedStatusHydrated={isHydrated}
                    />
                ) : null}
            </div>
        );
    };

    let handedness = 0;
    let progressionDurationData: IProgressionDurationChartValue[] = [];
    let progressionSmoothnessData: IProgressionSmoothnessChartValue[] = [];

    if (render && isPresent(activities)) {
        handedness = getActivityMetric("handedness", activity);

        progressionDurationData = activities
            ?.map((x) => ({
                Date: x?.creationDate,
                Duration: getActivityMetric("duration", x),
                ActivityInstanceId: x?.activityInstanceId,
            }))
            .sort((a, b) => {
                return new Date(a.Date).getTime() - new Date(b.Date).getTime();
            });

        progressionSmoothnessData = activities
            ?.map((x) => ({
                Date: x?.creationDate,
                Smoothness: getActivityMetric("smoothness", x),
                ActivityInstanceId: x?.activityInstanceId,
            }))
            .sort((a, b) => {
                return new Date(a.Date).getTime() - new Date(b.Date).getTime();
            });
    }

    const showProgressionDurationChart = progressionDurationData.length > 1;
    const showProgressionSmoothnessChart =
        progressionSmoothnessData.length > 1 &&
        progressionSmoothnessData
            .map((x) => x.Smoothness)
            .reduce((x, y) => {
                return x + y;
            }, 0) > 0;
    const speed = getActivityMetric("duration-percentile", activity);
    const precision = getActivityMetric("precision", activity);
    const smoothness = getActivityMetric("smoothness", activity);

    const getTags = () => {
        return activity.activityMeta?.tags
            ?.filter((x) => x.trim() !== "")
            .map((x) => (
                <Tag key={x.toString()} color={"cyan"}>
                    {prettifyName(x.toString())}
                </Tag>
            ));
    };

    const { distanceGraphOptions, handleFlagActivity, progressionDistanceDataItem, showProgressionDistanceChart } =
        useActivityView({ activity, canRender: render, activities });

    const { isUpdatingActivityMetadata, onUpdateActivityMetadata } = useUpdateActivityMetadata();

    const distance = distanceGraphOptions?.value;

    const radioChartData: IRadioChartData[] = [
        showProgressionDurationChart
            ? {
                  title: "Duration",
                  description: "Completion times over successive attempts for this activity",
                  options: {
                      showLabels: false,
                      currentAnnotation: { x: activity?.creationDate, y: activity?.duration },
                      values: progressionDurationData,
                      yField: "Duration",
                  },
              }
            : {},
        showProgressionSmoothnessChart
            ? {
                  title: "Smoothness",
                  description: "Smoothness over successive attempts for this activity",
                  options: {
                      showLabels: false,
                      currentAnnotation: {
                          x: activity.creationDate,
                          y: progressionSmoothnessData.find((item) => item.Date === activity.creationDate)?.Smoothness,
                      },
                      tooltipFormatter: smoothnessTooltipFormatter,
                      values: progressionSmoothnessData,
                      yAxisLabelFormatter: smoothnessYAxisLabelFormatterHandler,
                      yField: "Smoothness",
                  },
              }
            : {},
        showProgressionDistanceChart ? progressionDistanceDataItem : {},
    ].filter((datum) => isPresent(datum));

    const isOwner = activity?.userId === userId;
    const canCopy = hasCapability("Activity/Update", SystemContext);

    return (
        <section>
            <Row>
                <Col md={24} lg={24} xl={24}>
                    {render && hasMeta() && (
                        <Alert
                            style={{ margin: "0px 20px 6px 10px" }}
                            description={
                                <>
                                    {activity.activityMeta?.description}
                                    <span style={{ paddingLeft: "20px" }}>{getTags()}</span>
                                </>
                            }
                            type="info"
                            showIcon
                        />
                    )}
                    {render && !isFetchingMinimal ? (
                        isEditing ? (
                            <Col md={24} lg={14} xl={14} ref={scrollIntoView}>
                                <div className="newUI-glass">
                                    <EditActivityMetadata
                                        disabled={isUpdatingActivityMetadata}
                                        hasButtons
                                        initialValues={{
                                            ...activity.activityMeta,
                                            activityInstanceId: activity.activityInstanceId,
                                            creationDate: moment(activity.creationDate),
                                        }}
                                        isUpdating={isUpdatingActivityMetadata}
                                        onClose={() => setIsEditing(false)}
                                        onUpdate={onUpdateActivityMetadata}
                                    />
                                </div>
                            </Col>
                        ) : (
                            renderVideo(activity)
                        )
                    ) : (
                        <LargeSpinner />
                    )}
                    {render && hasVideo && isOwner && (
                        <VideoInvite activity={activity} userConnections={userConnections} />
                    )}
                    {render && !hasVideo && hasSignature() && (
                        <div style={{ margin: "10px 20px 0px 10px" }}>
                            <SignatureWidget signature={activity?.activitySignature} />
                        </div>
                    )}
                    {render && (isOwner || canCopy) && (
                        <Space style={{ marginTop: "20px" }}>
                            {isOwner && (
                                <>
                                    {!isEditing ? (
                                        <IconButton
                                            className="secondary"
                                            icon={<EditFilled />}
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </IconButton>
                                    ) : null}
                                    <Popconfirm
                                        title="Delete this activity completely? This cannot be undone."
                                        okText="Yes, delete the activity"
                                        cancelText="Cancel"
                                        className="confirmation"
                                        onConfirm={deleteActivity}
                                        cancelButtonProps={{ className: "secondary" }}
                                    >
                                        <IconButton
                                            icon={<CloseCircleFilled />}
                                            className="secondary danger"
                                            title="Delete activity"
                                        >
                                            Delete activity
                                        </IconButton>
                                    </Popconfirm>
                                    {hasVideo ? (
                                        <IconButton
                                            className="secondary danger"
                                            style={{ width: 50 }}
                                            icon={<FlagOutlined />}
                                            onClick={handleFlagActivity}
                                        />
                                    ) : null}
                                </>
                            )}
                            {hasVideo && canCopy ? <ActivityCopy activity={activity} /> : null}
                        </Space>
                    )}
                </Col>
            </Row>
            <Divider />
            <Row className={"newUI-raised"}>
                <Title level={3}>Completion Time</Title>
                <span style={{ marginBottom: "30px", display: "block", width: "100%" }}>
                    Operating duration against the average
                </span>
                <Col md={24} lg={24} xl={24}>
                    {render ? (
                        <HorizontalBar
                            values={[
                                getActivityMetric("duration", activity),
                                getActivityMetric("duration-median", activity),
                            ]}
                            labels={["User duration", "Global average"]}
                            timeFormat={true}
                        />
                    ) : (
                        <LargeSpinner />
                    )}
                </Col>
            </Row>
            <Divider />
            {!(precision === 0 && smoothness === 0 && speed === 0) && (
                <div className={"inset-section"} style={{ paddingBottom: "30px" }}>
                    <Row>
                        <Title level={3} style={{ width: "100%" }}>
                            Metrics
                        </Title>
                        <span style={{ marginBottom: "30px", display: "block" }}>
                            Smoothness, speed, precision and handedness
                        </span>
                    </Row>
                    <Row>
                        <Col md={24} lg={12} xl={distance > 0 ? 8 : 12}>
                            {render ? <MetricsPercentileGraph activity={activity} /> : <LargeSpinner />}
                        </Col>
                        <Col md={24} lg={12} xl={distance > 0 ? 8 : 12}>
                            {render ? (
                                handedness > 0 && <HandednessGraph percentage={[handedness, 100 - handedness]} />
                            ) : (
                                <LargeSpinner />
                            )}
                        </Col>
                        <Col md={24} lg={12} xl={8}>
                            {render ? distance > 0 && <ColumnGraph {...distanceGraphOptions} /> : <LargeSpinner />}
                        </Col>
                    </Row>
                </div>
            )}
            <Divider />

            <Row>
                {(showProgressionDurationChart || showProgressionSmoothnessChart || showProgressionDistanceChart) && (
                    <>
                        <Title level={3}>Progression</Title>
                    </>
                )}
            </Row>
            <Row>
                <Col md={24} lg={24} xl={24}>
                    {!render || progressionQuery.isFetching ? (
                        <LargeSpinner />
                    ) : (
                        <RadioChart
                            options={{
                                chartType: "area",
                                handleActivitySelect,
                                title: "",
                                tooltipFormatter,
                                tooltipTitle,
                                xAxisLabelFormatter: (val: string): string => moment(val).format("MMM Do YYYY"),
                                xField: "Date",
                                userId: activity.userId,
                                yAxisLabelFormatter: yAxisLabelFormatterHandler,
                            }}
                            data={radioChartData}
                        />
                    )}
                </Col>
            </Row>
        </section>
    );
};

export default memo(ActivityView);
